import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw, Wallet } from 'lucide-react';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { barberInput } from '@barbeiropro/contracts';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useBranches } from '@/lib/org';
import { useSession } from '@/lib/session';
import { useAssetUrl, type BarberRow } from './api';
import { applyServerError, zodForm } from './lib';

export const useBarberList = (opts: { includeInactive?: boolean; includeDeleted?: boolean }) =>
  useQuery({
    queryKey: ['barbers', 'list', opts],
    queryFn: () => api.get<BarberRow[]>('/barbers', { includeInactive: opts.includeInactive ? 'true' : undefined, includeDeleted: opts.includeDeleted ? 'true' : undefined }),
  });

export function BarberAvatar({ b, className }: { b: Pick<BarberRow, 'displayName' | 'photoAssetId' | 'color'>; className?: string }) {
  const url = useAssetUrl(b.photoAssetId);
  return (
    <Avatar className={className} style={{ boxShadow: `0 0 0 2px ${b.color}` }}>
      {url && <AvatarImage src={url} alt="" />}
      <AvatarFallback>{b.displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
    </Avatar>
  );
}

export function TeamPage() {
  const qc = useQueryClient();
  const { can } = useSession();
  const [deleted, setDeleted] = useQueryState('excluidos', parseAsBoolean.withDefault(false));
  const [inactive, setInactive] = useQueryState('inativos', parseAsBoolean.withDefault(true));
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const barbers = useBarberList({ includeInactive: inactive || deleted, includeDeleted: deleted });
  const branches = useBranches();
  const branchName = (id: string) => branches.data?.find((b) => b.id === id)?.name ?? '-';

  const restore = useMutation({
    mutationFn: (b: BarberRow) => api.post(`/barbers/${b.id}/restore`),
    onSuccess: () => {
      toast.success('Profissional restaurado');
      qc.invalidateQueries({ queryKey: ['barbers'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = (barbers.data ?? []).filter((b) => b.displayName.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Equipe"
        description="Profissionais, serviços que atendem, horários e acesso ao sistema."
        actions={
          <>
            {can('commissions.manage') && (
              <Button variant="outline" asChild>
                <Link to="/app/comissoes">
                  <Wallet aria-hidden /> Comissões
                </Link>
              </Button>
            )}
            {can('catalog.manage') && (
              <Button onClick={() => setNewOpen(true)}>
                <Plus aria-hidden /> Novo profissional
              </Button>
            )}
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar profissional" aria-label="Buscar profissional" className="max-w-xs" />
        <Button variant={inactive && !deleted ? 'default' : 'outline'} size="sm" aria-pressed={inactive} disabled={deleted} onClick={() => setInactive(!inactive)}>
          Mostrar inativos
        </Button>
        <Button variant={deleted ? 'default' : 'outline'} size="sm" aria-pressed={deleted} onClick={() => setDeleted(!deleted)}>
          Excluídos
        </Button>
      </div>

      {barbers.isLoading ? (
        <LoadingState />
      ) : barbers.error ? (
        <ErrorState error={barbers.error} onRetry={() => barbers.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={deleted ? 'Nenhum profissional excluído' : 'Nenhum profissional cadastrado'}
          description={deleted ? undefined : 'Cadastre quem atende na barbearia para abrir a agenda.'}
          action={!deleted && can('catalog.manage') ? <Button onClick={() => setNewOpen(true)}><Plus aria-hidden /> Novo profissional</Button> : undefined}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Profissional</TableHead>
                <TableHead>Unidade</TableHead>
                <TableHead>Cadeira</TableHead>
                <TableHead>Serviços</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <BarberAvatar b={b} />
                      <div className="min-w-0">
                        {b.deletedAt ? (
                          <span className="font-medium">{b.displayName}</span>
                        ) : (
                          <Link to={`/app/equipe/${b.id}`} className="font-medium underline-offset-4 hover:underline">
                            {b.displayName}
                          </Link>
                        )}
                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="inline-block size-2.5 rounded-full border" style={{ backgroundColor: b.color }} aria-hidden />
                          Cor na agenda {b.color.toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{branchName(b.branchId)}</TableCell>
                  <TableCell>{b.chairName ?? <span className="text-muted-foreground">Sem cadeira</span>}</TableCell>
                  <TableCell className="tabular">{b.serviceIds.length === 0 ? <span className="text-oxblood-ink">Nenhum</span> : b.serviceIds.length}</TableCell>
                  <TableCell>
                    {b.deletedAt ? (
                      <Badge variant="outline">Excluído</Badge>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={b.active ? 'secondary' : 'outline'}>{b.active ? 'Ativo' : 'Inativo'}</Badge>
                        {!b.publicVisible && <Badge variant="outline">Oculto no site</Badge>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {b.deletedAt ? (
                      can('catalog.manage') && (
                        <Button variant="outline" size="sm" onClick={() => restore.mutate(b)} disabled={restore.isPending}>
                          <RotateCcw aria-hidden /> Restaurar
                        </Button>
                      )
                    ) : (
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/app/equipe/${b.id}`}>Abrir ficha</Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewBarberModal open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

const newSchema = barberInput.pick({ displayName: true, branchId: true });
type NewValues = z.input<typeof newSchema>;

function NewBarberModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const branches = useBranches();
  const form = useForm<NewValues>({ resolver: zodForm<NewValues>(newSchema), defaultValues: { displayName: '', branchId: '' } });
  const { errors } = form.formState;
  const create = useMutation({
    mutationFn: (v: NewValues) => api.post<{ id: string }>('/barbers', barberInput.parse({ ...v })),
    onSuccess: (b) => {
      toast.success('Profissional salvo');
      qc.invalidateQueries({ queryKey: ['barbers'] });
      onOpenChange(false);
      navigate(`/app/equipe/${b.id}`);
    },
    onError: (e) => applyServerError(form, e),
  });
  useEffect(() => {
    if (open && !form.getValues('branchId') && branches.data?.[0]) form.setValue('branchId', branches.data[0].id);
  }, [open, branches.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormModal
      open={open}
      onOpenChange={(v) => {
        if (!v) form.reset({ displayName: '', branchId: '' });
        onOpenChange(v);
      }}
      title="Novo profissional"
      description="Depois do cadastro você completa foto, serviços e horários na ficha."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="new-barber-form" disabled={create.isPending}>
            {create.isPending ? 'Salvando…' : 'Salvar profissional'}
          </Button>
        </>
      }
    >
      {branches.data && branches.data.length === 0 ? (
        <EmptyState title="Cadastre uma unidade primeiro" description="Todo profissional pertence a uma unidade." action={<Button asChild><Link to="/app/configuracoes/unidades">Ir para unidades</Link></Button>} />
      ) : (
        <form id="new-barber-form" onSubmit={form.handleSubmit((v) => create.mutate(v))} className="grid gap-4" noValidate>
          <Field id="nb-name" label="Nome de exibição" error={errors.displayName?.message}>
            <Input id="nb-name" autoFocus aria-invalid={!!errors.displayName} {...form.register('displayName')} />
          </Field>
          <Field id="nb-branch" label="Unidade" error={errors.branchId?.message}>
            <Controller
              control={form.control}
              name="branchId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="nb-branch" className="w-full">
                    <SelectValue placeholder="Escolha a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.data?.filter((b) => b.active).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </form>
      )}
    </FormModal>
  );
}
